/**
 * UTM + Click ID + Meta cookies helpers.
 * Captures attribution data from URL/cookies on first visit, persists across the session,
 * and attaches everything to the lead payload so the admin can run manual conversion APIs
 * (Meta CAPI, Google Enhanced Conversions, TikTok Events API) if a webhook fails.
 */
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
const CLICK_ID_KEYS = ["fbclid", "gclid", "ttclid", "msclkid", "twclid", "li_fat_id", "wbraid", "gbraid"];

function readCookie(name) {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : null;
}

function buildFbc(fbclid) {
  // Meta CAPI expects fbc in the format: `fb.1.<timestamp_ms>.<fbclid>`
  if (!fbclid) return null;
  return `fb.1.${Date.now()}.${fbclid}`;
}

export function captureUtmFromUrl() {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);

  // === UTMs ===
  const utm = {};
  UTM_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v) utm[k] = v;
  });
  if (Object.keys(utm).length > 0) {
    try { sessionStorage.setItem("utm", JSON.stringify(utm)); } catch (_) { /* noop */ }
    try { localStorage.setItem("utm", JSON.stringify(utm)); } catch (_) { /* noop */ }
  }

  // === Click IDs ===
  const clicks = {};
  CLICK_ID_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v) clicks[k] = v;
  });
  if (Object.keys(clicks).length > 0) {
    try { sessionStorage.setItem("click_ids", JSON.stringify(clicks)); } catch (_) { /* noop */ }
    try { localStorage.setItem("click_ids", JSON.stringify(clicks)); } catch (_) { /* noop */ }
  }

  // === Landing URL (full first-touch URL) ===
  try {
    if (!sessionStorage.getItem("landing_url")) {
      sessionStorage.setItem("landing_url", window.location.href);
    }
  } catch (_) { /* noop */ }

  // === list (campaign routing) ===
  const list = params.get("list");
  if (list) {
    try { sessionStorage.setItem("list", list); } catch (_) { /* noop */ }
  }

  // === Everflow attribution (?sub1=<affiliate_slug>&_ef_transaction_id=...) ===
  // `sub1` doubles as the affiliate `list_id` — same value the affiliate configured in Everflow.
  // If both `list` and `sub1` are present, `list` takes precedence (explicit override).
  const sub1 = params.get("sub1");
  if (sub1 && !list) {
    try { sessionStorage.setItem("list", sub1); } catch (_) { /* noop */ }
    try { sessionStorage.setItem("aff_sub1", sub1); } catch (_) { /* noop */ }
  } else if (sub1) {
    try { sessionStorage.setItem("aff_sub1", sub1); } catch (_) { /* noop */ }
  }
  const ef = params.get("_ef_transaction_id") || params.get("ef_transaction_id");
  if (ef) {
    try { sessionStorage.setItem("everflow_transaction_id", ef); } catch (_) { /* noop */ }
    try { localStorage.setItem("everflow_transaction_id", ef); } catch (_) { /* noop */ }
  }
}

export function getUtm() {
  try {
    const raw = sessionStorage.getItem("utm") || localStorage.getItem("utm");
    return raw ? JSON.parse(raw) : {};
  } catch (_) {
    return {};
  }
}

export function getClickIds() {
  try {
    const raw = sessionStorage.getItem("click_ids") || localStorage.getItem("click_ids");
    return raw ? JSON.parse(raw) : {};
  } catch (_) {
    return {};
  }
}

/**
 * Returns Meta's _fbp + _fbc cookies (formatted for CAPI).
 * - `_fbp` is set by the Pixel script itself.
 * - `_fbc` is set by Pixel when fbclid is in URL — fallback: build from sessionStorage fbclid.
 */
export function getMetaCookies() {
  const fbp = readCookie("_fbp");
  let fbc = readCookie("_fbc");
  if (!fbc) {
    const { fbclid } = getClickIds();
    if (fbclid) fbc = buildFbc(fbclid);
  }
  return { fbp: fbp || null, fbc: fbc || null };
}

export function getListOverride() {
  try {
    return sessionStorage.getItem("list") || null;
  } catch (_) {
    return null;
  }
}

export function getEverflowSub1() {
  try {
    return sessionStorage.getItem("aff_sub1") || null;
  } catch (_) {
    return null;
  }
}

export function getEverflowTransactionId() {
  try {
    return sessionStorage.getItem("everflow_transaction_id")
      || localStorage.getItem("everflow_transaction_id")
      || null;
  } catch (_) {
    return null;
  }
}

export function getLandingUrl() {
  try {
    return sessionStorage.getItem("landing_url") || (typeof window !== "undefined" ? window.location.href : null);
  } catch (_) {
    return null;
  }
}

/**
 * Returns the full attribution bundle to attach to the lead payload.
 * Includes UTMs + click IDs + Meta cookies + landing_url + referrer + UA + screen + Everflow txn id.
 */
export function getAttributionPayload() {
  const utm = getUtm();
  const clicks = getClickIds();
  const meta = getMetaCookies();
  return {
    ...utm,
    ...clicks,
    fbp: meta.fbp,
    fbc: meta.fbc,
    landing_url: getLandingUrl(),
    referrer: typeof document !== "undefined" ? (document.referrer || null) : null,
    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    screen_resolution: typeof window !== "undefined" && window.screen ? `${window.screen.width}x${window.screen.height}` : null,
    language: typeof navigator !== "undefined" ? (navigator.language || null) : null,
    everflow_transaction_id: getEverflowTransactionId(),
  };
}
